import styled from 'styled-components';
import {getColorFromTheme} from '../../../../../../../ui/ui-utils';

export const GoalSettingsPage = styled.main`
  width: 100%;
  max-width: 1120px;
  min-width: 0;
  padding-bottom: 48px;
`;

export const GoalSettingsIntro = styled.header`
  margin-bottom: 24px;

  h1 {
    margin: 0 0 4px;
    color: ${getColorFromTheme('blackTwo')};
    font: 600 24px/32px Graphik, sans-serif;
  }

  p {
    margin: 0;
    color: ${getColorFromTheme('textBody')};
    font: 400 16px/24px Graphik, sans-serif;
  }
`;

export const GoalSettingsBody = styled.section`
  display: grid;
  gap: 24px;
  width: 100%;
  min-width: 0;
`;

export const SettingsCard = styled.section`
  background: ${getColorFromTheme('white')};
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  min-width: 0;
  padding: 24px;
`;

export const SettingsCardTitle = styled.h2`
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  color: ${getColorFromTheme('blackTwo')};
  font: 600 16px/24px Graphik, sans-serif;
  margin: 0;
  padding: 0 0 16px;
`;

export const SettingRow = styled.div`
  align-items: start;
  display: grid;
  gap: 32px;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 336px);
  padding: 24px 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  }

  &:last-child {
    padding-bottom: 0;
  }

  @media (max-width: 800px) {
    gap: 16px;
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const FeatureRow = styled(SettingRow)`
  align-items: center;
  grid-template-columns: minmax(0, 1fr) auto;

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr) auto;
  }
`;

export const SettingCopy = styled.div`
  min-width: 0;
`;

export const SettingTitleLine = styled.div`
  align-items: center;
  color: ${getColorFromTheme('blackTwo')};
  display: flex;
  font: 600 14px/20px Graphik, sans-serif;
  gap: 6px;
  min-width: 0;

  svg {
    flex: 0 0 auto;
    height: 14px;
    width: 14px;
  }
`;

export const SettingDescription = styled.p`
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/20px Graphik, sans-serif;
  margin: 4px 0 0;
  max-width: 590px;
`;

export const DisplayNameControl = styled.div`
  min-width: 0;
`;

export const SettingsInput = styled.input`
  background: ${getColorFromTheme('white')};
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 10px;
  color: ${getColorFromTheme('blackTwo')};
  font: 400 14px/20px Graphik, sans-serif;
  height: 48px;
  outline: none;
  padding: 0 12px;
  width: 100%;

  &:focus {
    border-color: ${getColorFromTheme('purple300')};
    box-shadow: 0 0 0 3px rgba(88, 90, 223, 0.12);
  }

  &:disabled,
  &[readonly] {
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textMuted')};
    cursor: not-allowed;
  }
`;

export const NameActions = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: 74px minmax(0, 1fr);
  margin-top: 8px;

  button {
    align-items: center;
    box-sizing: border-box;
    display: inline-flex;
    height: 40px;
    justify-content: center;
    line-height: 20px;
    padding: 8px 16px;
  }
`;

export const ResetNameButton = styled.button`
  background: transparent;
  border: 0;
  color: ${getColorFromTheme('purple300')};
  cursor: pointer;
  display: block;
  font: 500 12px/18px Graphik, sans-serif;
  margin: 6px 0 0 auto;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

export const SettingsToggleWrap = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 32px;
  min-width: 40px;

  .react-switch-label {
    height: 20px !important;
    opacity: 1;
    transition: background-color 180ms ease, opacity 180ms ease !important;
    width: 32px !important;
  }

  .react-switch-button {
    background: ${getColorFromTheme('white')} !important;
    height: 16px !important;
    transition: left 180ms cubic-bezier(0.4, 0, 0.2, 1),
      transform 180ms cubic-bezier(0.4, 0, 0.2, 1), background-color 180ms ease,
      box-shadow 180ms ease !important;
    width: 16px !important;
  }

  input:disabled + .react-switch-label {
    opacity: 0.72;
  }

  .react-switch-label:active .react-switch-button {
    width: 16px !important;
  }
`;

export const ConfirmationDialog = styled.div`
  background: ${getColorFromTheme('white')};
  border-radius: 12px;
  box-shadow: 0 18px 48px rgba(30, 30, 47, 0.18);
  margin: 16vh auto 0;
  max-width: 500px;
  overflow: hidden;
  width: calc(100% - 32px);
`;

export const ConfirmationHeader = styled.div`
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
  padding: 24px;

  h2 {
    color: ${getColorFromTheme('blackTwo')};
    font: 600 20px/28px Graphik, sans-serif;
    margin: 0;
  }
`;

export const ConfirmationBody = styled.div`
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
  padding: 24px;

  p {
    margin: 0;
  }
`;

export const ConfirmationFooter = styled.div`
  align-items: center;
  background: ${getColorFromTheme('backgroundDark')};
  border-top: 1px solid ${getColorFromTheme('borderLight')};
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 16px 24px;

  button {
    height: 42px;
    padding: 9px 18px;
  }
`;
